import { useState } from "react";
import { BookOpen, Database, EyeOff, Table2 } from "lucide-react";
import { TrustBadge } from "./TrustBadge";

const TABS = [
  { id: "numbers", label: "Numbers", Icon: Table2 },
  { id: "definitions", label: "Definitions", Icon: BookOpen },
  { id: "sources", label: "Sources", Icon: Database },
];

export const EvidencePanel = ({ result, children, testId = "evidence-panel" }) => {
  const [tab, setTab] = useState("numbers");
  const a = result?.answerability;
  const withheld = result?.withheld || [];

  return (
    <div className="flex flex-col h-full bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={testId}>
      <div className="px-5 pt-5 pb-4 border-b border-stone-200 space-y-3.5 bg-white">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-[12px] font-semibold text-stone-600">
              Evidence
            </div>
            <h2 className="text-base font-semibold text-stone-900 font-display">
              {KIND_LABELS[result?.kind] || "Computed result"}
            </h2>
          </div>
        </div>
        <TrustBadge answerability={a} testId={`${testId}-trust-badge`} />
        <div className="flex gap-1">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`${testId}-tab-${id}`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                tab === id
                  ? "bg-stone-900 text-white"
                  : "text-stone-600 hover:bg-stone-200/70"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-5">
        {tab === "numbers" && (
          <div className="space-y-6">
            {children || <Empty text="Nothing computed for this answer." />}
            <ContextCitations citations={result?.context_citations} testId={testId} />
          </div>
        )}

        {tab === "definitions" && (
          <div className="space-y-3" data-testid={`${testId}-definitions`}>
            {(a?.metrics || []).length === 0 && <Empty text="No metric was invoked." />}
            {(a?.metrics || []).map((m) => (
              <div key={m.id} className="border border-stone-200 rounded-lg p-3 bg-stone-50/50">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-stone-900">{m.label}</span>
                  <span className="num text-[12px] text-stone-600">
                    {m.id} · v{m.version} · {m.question}
                  </span>
                  {m.status === "refused" && (
                    <span className="text-[12px] font-semibold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                      refused
                    </span>
                  )}
                </div>
                {m.definition && (
                  <p className="text-xs text-stone-700 mt-1.5 leading-relaxed">{m.definition}</p>
                )}
                {m.formula && (
                  <pre className="num text-[12px] text-stone-700 bg-white border border-stone-200 rounded px-2 py-1.5 mt-2 whitespace-pre-wrap">
                    {m.formula}
                  </pre>
                )}
                {m.requires && (
                  <p className="text-[12px] text-stone-600 mt-1.5">
                    <span className="font-semibold">Requires:</span> {m.requires}
                  </p>
                )}
                {m.guard && (
                  <p className="text-[12px] text-amber-800 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    {m.guard}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "sources" && (
          <div className="space-y-4" data-testid={`${testId}-sources`}>
            <div>
              <SectionLabel>Source datasets</SectionLabel>
              <ul className="space-y-1.5 mt-2">
                {(a?.sources || []).map((s, i) => (
                  <li key={i} className="text-xs text-stone-700 leading-relaxed flex gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#A01236] mt-1.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
                {(a?.sources || []).length === 0 && <Empty text="No dataset was read." />}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fact label="Sample size (n)" value={a?.sample_size ?? "—"} />
              <Fact label="k-anonymity floor" value={a?.k_floor ?? "—"} />
              <Fact label="FY range" value={a?.fy_range?.join(", ") || "—"} />
              <Fact label="Geo match (worst)" value={a?.geo?.worst_level || "—"} />
            </div>
            {withheld.length > 0 && (
              <div>
                <SectionLabel>
                  <EyeOff className="w-3 h-3 inline mr-1 -mt-0.5" />
                  Withheld metrics
                </SectionLabel>
                <div className="space-y-2 mt-2">
                  {withheld.map((m) => (
                    <div
                      key={m.id}
                      className="border border-rose-200 bg-rose-50/60 rounded-lg p-3"
                      data-testid={`withheld-${m.id}`}
                    >
                      <div className="text-xs font-semibold text-stone-900">{m.label}</div>
                      <p className="text-[12px] text-stone-700 mt-1 leading-relaxed">{m.evidence}</p>
                      <p className="text-[12px] text-stone-600 mt-1.5 leading-relaxed">
                        <span className="font-semibold">Unlocks with:</span> {m.unlocks_with}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const KIND_LABELS = {
  board_note: "Board note — the whole portfolio",
  state_brief_multi: "State briefing",
  district_profile: "District profile",
  aspirational_districts: "Aspirational districts",
  peer_benchmarking_mca_basis: "Ten-year MCA basis",
  partner_performance: "Partner performance",
  reallocation: "Spend concentration & reallocation",
  peer_benchmarking: "Peer presence & crowding (MCA)",
  peer_benchmarking_grid: "Peer crowding by state (MCA)",
  need_vs_funding: "Need vs funding — district (placeholder data)",
  need_vs_funding_state: "Need vs funding — state",
  data_reality: "Data reality",
  refusal: "Refusal",
  unsupported: "Outside the metric registry",
};

const SectionLabel = ({ children }) => (
  <div className="text-[12px] font-semibold text-stone-600">{children}</div>
);

const Fact = ({ label, value }) => (
  <div className="border border-stone-200 rounded-lg px-3 py-2 bg-stone-50/60">
    <div className="text-[12px] text-stone-600">{label}</div>
    <div className="num text-sm text-stone-900 mt-0.5">{String(value)}</div>
  </div>
);

const Empty = ({ text }) => <p className="text-xs text-stone-600 py-4">{text}</p>;

/** External material that frames an answer. Deliberately separated from the computed numbers. */
const ContextCitations = ({ citations, testId }) => {
  const loaded = (citations || []).filter((c) => c.status === "loaded");
  if (loaded.length === 0) return null;
  return (
    <section className="space-y-2.5 pt-1" data-testid={`${testId}-context`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[12px] font-semibold text-stone-600">
          Context, not evidence
        </h3>
        <span className="text-[12px] text-stone-600">never computed with</span>
      </div>
      {loaded.map((c) => (
        <div key={c.id} className="rounded-lg border border-dashed border-stone-300 bg-stone-50/70 p-3.5 space-y-2"
             data-testid={`context-citation-${c.id}`}>
          <div className="text-[13px] font-semibold text-stone-900">
            {c.title}
            <span className="font-normal text-stone-600"> · {c.publisher}, {c.year}</span>
          </div>
          {(c.claims || []).map((x, i) => (
            <p key={i} className="text-[13px] text-stone-700 leading-relaxed border-l-2 border-stone-300 pl-2.5">
              {x.claim}
              <span className="block text-[12px] num text-stone-500 mt-1">{x.reference}</span>
            </p>
          ))}
          <p className="text-[12px] text-stone-600 leading-relaxed">
            Cannot support: {c.cannot_support}
          </p>
        </div>
      ))}
    </section>
  );
};
